import React, { memo, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    CUSTOMER_PRODUCTS,
    useGetCustomerProductsPrices,
    useUpdateCustomerProductPrice
} from 'hooks/customer/products.hooks';
import {
    Avatar,
    Box,
    Button,
    Chip,
    Grid,
    IconButton,
    InputAdornment,
    TableCell,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import PricesFilterBar from './PricesFilterBar';
import MasterTable from 'ui-component/master-table/MasterTable';
import Loader from 'ui-component/Loader';
import { HeadCell } from 'types';
import IItem from 'types/product_delivery';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { dateObjectToString, roundDecimals } from '../../../../fromKotlin/nkutils';
import { CurrencyShekel, Percentage } from 'tabler-icons-react';
import { copyClientPrice, updateAllPrice, updateCustomerProductAvailable } from '../../../../api/customer/products_prices.api';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import {
    getCustomers,
    getSpecificCustomer,
    selectActiveCustomers,
    selectCustomerById
} from '../../../../store/customers/customersSlice';
import AllProductPriceChangesDialog from '../../../../ui-component/mass-changes-dialogs/massPriceChangeDialogs';
import { useQueryClient } from 'react-query';
import {CheckCircleOutline, CopyAll, PriceChangeOutlined} from '@mui/icons-material';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import DialogChooseAction from '../../../../ui-component/dialogs/SelectionDialog';
import Customer from '../../../../types/customer';
import { SNACKBAR_OPEN } from '../../../../store/actions';
import Swal from 'sweetalert2';
import NoNegative from '../../../../ui-component/textFieldNumber';
import { handleKeyDown } from '../../../../utils/tableAsKeyBorad';
import PricesPdfExport from './PricesPdfExport';
import { pdf } from "../../../../assets/images";
import AgTable, { ITableControls } from 'ui-component/ag-grid/Ag-Table';
import { numberCellRenderer } from 'ui-component/ag-grid/ag-table-utils';
import { IconInfoCircle } from '@tabler/icons';
import JSDB from "../../../../fromKotlin/nk";
import { IAgChip } from 'ui-component/ag-grid/components/Ag-Chip';
import basicPinnedControl from "../../../../ui-component/ag-grid/utils/basicPinnedTopRow";
import MyChip from "../../../../ui-component/extended/Chip";
import AllProductAvailableChangesDialog from "../../../../ui-component/mass-changes-dialogs/massAvailableChangeDialogs";
import AvailabilityCopyDialog from "../../../../ui-component/mass-changes-dialogs/availabilityCopyDialog";
import { cloneDeep } from 'lodash';
import { ImportWizardTrigger } from 'ui-component/import-wizard';
import { buildCustomerPricesImportAdapter } from 'api/customer/importCustomerPricesAdapter';
import PriceHistoryDialog, { PriceHistoryTarget } from './PriceHistoryDialog';

const columns: HeadCell[] = [
    {
        id: 'checkbox',
        numeric: false,
        label: '',
        align: 'center',
        type: 'string',
    },
    { id: 'id', numeric: false, label: 'מזהה', align: 'center', type: 'string' },
    { id: 'external_id', numeric: false, label: 'מזהה חיצוני', align: 'center',type:'numOrStr', hide: true },
    { id: 'name', numeric: false, label: 'שם מוצר', align: 'center', type: 'string', minColWidth: 160 },
    { id: 'barcode', numeric: false, label: 'ברקוד', align: 'center', type: 'string' },
    { id: 'category', numeric: false, label: 'קטגוריה', align: 'center', type: 'string', hide: true, enableRowGroup: true },
    { id: 'group', numeric: false, label: 'קבוצה', align: 'center', type: 'string', hide: true },
    { id: 'unit', numeric: false, label: 'יחידות', align: 'center', type: 'string', hide: true },

    {
        id: 'totalPrice',
        numeric: true,
        label: 'מחיר',
        align: 'center',
        type: 'number',
        filterable: false,
        cellRenderer: ({ params }) => {
            return numberCellRenderer(params, "currency")
        },
        editable: true,
        pinnedRowProps: {
            action: "avg",
            format: "currency"
        },
        dataFormat: "currency"
    },
    {
        id: 'discount',
        numeric: true,
        label: 'הנחה',
        align: 'center',
        type: 'number',
        filterable: false,
        cellRenderer: ({ params }) => {
            return numberCellRenderer(params, "percentage")
        },
        editable: true,
        pinnedRowProps: {
            action: "avg",
            format: "currency"
        },
        dataFormat: "percent",
        valueLimit: {
            max: 100,
            min: -100
        }
    },
    {
        id: 'after',
        numeric: true,
        label: 'לאחר הנחה',
        align: 'center',
        type: 'number',
        pinnedRowProps: {
            action: "avg",
            format: "currency"
        },
        extractFunc: (row) => {
            return roundDecimals(row.totalPrice * (1 - row.discount / 100));
        },
        filterable: false,
        cellRenderer: ({ params }) => {
            return numberCellRenderer(params, "currency")
        },
        dataFormat: "currency"
    },
    {
        id: 'totalPriceInclude',
        numeric: true,
        label: 'מחיר כולל',
        align: 'center',
        type: 'number',
        hide: true,
        filterable: false,
        pinnedRowProps: {
            action: "avg",
            format: "currency"
        },
        extractFunc: (row) => roundDecimals(row?.totalPriceInclude ?? 0, 2),
        cellRenderer: ({ params }) => {
            return numberCellRenderer(params, "currency")
        },
        dataFormat: "currency"
    },
    {
        id: 'totalPriceExclude',
        numeric: true,
        label: 'מחיר לא כולל',
        align: 'center',
        type: 'number',
        hide: true,
        filterable: false,
        pinnedRowProps: {
            action: "avg",
            format: "currency"
        },
        extractFunc: (row) => roundDecimals(row?.totalPriceExclude ?? 0, 2),
        cellRenderer: ({ params }) => {
            return numberCellRenderer(params, "currency")
        },
        dataFormat: "currency"
    },
    {
        id: 'costPrice',
        numeric: true,
        label: 'מחיר עלות',
        align: 'center',
        hide: true,
        type: 'number',
        pinned: "left",
        filterable: false,
        cellRenderer: ({ params }) => {
            return numberCellRenderer(params, "currency")
        }
    },    {
        id: 'costPriceGain',
        numeric: true,
        label: 'רווח(%)',
        align: 'center',
        hide: true,
        type: 'number',

        filterable: false,
        extractFunc: (row) => {
            if(row.totalPrice === 0) return 0;
            return roundDecimals((row.totalPrice - row.costPrice)/(row.totalPrice)*100);
        },
        cellRenderer: ({ params }) => {
            return numberCellRenderer(params, "percentage")
        }
    },  {
        id: 'costPriceGain',
        numeric: true,
        label: 'רווח(שח)',
        align: 'center',
        hide: true,
        type: 'number',

        filterable: false,
        extractFunc: (row) => {
            if(row.totalPrice === 0) return 0;
            return roundDecimals((row.totalPrice - row.costPrice));
        },
        cellRenderer: ({ params }) => {
            return numberCellRenderer(params, "currency")
        }
    },
    {
        id: 'available',
        numeric: false,
        label: 'זמין להזמנה',
        align: 'center',
        hide: true,
        filterable: false,
        editable: true,
        cellRenderer({ params }) {
            if (params?.node?.group) {
                if (params?.value) return (params?.value === true || params?.value === "true") ? 'כן זמין' : 'לא זמין';1    ;;      1;
                return ""
            }
            return (
                <>
                    {params?.data?.available ?
                        <span className='ag-icon ag-icon-tick content-icon' />
                        :
                        <span className='ag-icon ag-icon-cross content-icon' />}
                </>
            )
        }
    },
    {
        id: 'master',
        numeric: true,
        label: 'מוצר אבא לחיוב',
        align: 'center',
        hide: true,
        filterable: false,
        editable: true,

    },
];

const MultiProductsActions = (selected) => (
    <>
        <PricesPdfExport
            clientId={Number(selected.customer.nkObject.masterBranch?.id ?? selected.customer.id)}
            clientName={selected.customer.name}
            date={selected.date}
            productIds={selected.selected}
            label="ייצא מחירים לPDF"
        />
        {selected.noPrice ? undefined:<>
            <AllProductPriceChangesDialog data={selected} type={0} icon={<Chip
                icon={<CurrencyShekel
                />}
                label={'שינוי מחיר למסומנים'}
                style={{ cursor: 'pointer' }}
            />} tooltip={'שינוי מחיר גורף'} />
            <AllProductPriceChangesDialog data={selected} type={1} icon={<Chip
                icon={<Percentage />}
                label={'שינוי הנחה למסומנים'}
                style={{ cursor: 'pointer' }}
            />} tooltip={'שינוי הנחה גורף'} />

        </>}


        <AllProductAvailableChangesDialog data={selected} type={0} icon={
            <MyChip
                icon={<CheckCircleOutline />}
                label={'שינוי זמינות למסומנים'}
                variant="outlined"
                sx={{
                    ml: 0.5, mr: 0.5, '& .MuiChip-icon': {
                        color: '#000080',
                    },
                }}
            />} tooltip={'שינוי זמינות גורף'}
                                           />
    </>
);


const PricesList = ({ cid }: { cid: string }) => {
    const dispatch = useDispatch();
    const [checked, setChecked] = React.useState(false);

    const [radio, setRadio] = React.useState('from');
    const [date, setDate] = React.useState(new Date());
    const [toDate, setToDate] = React.useState(undefined);
    const [isMutating, setIsMutating] = React.useState(false);
    const [sold, setSold] = React.useState(undefined);
    const [onlytAvailable, setOnlyAvailable] = React.useState( localStorage.getItem('onlyAvailable') === 'true');
    const [heads, setHead] = React.useState<HeadCell[]>(columns);
    const customers: Customer[] = useSelector(selectActiveCustomers);


    React.useEffect(() => {
        if (!customers || customers.length == 0)
            dispatch(getCustomers());
    }, [dispatch]);
    const cache = useQueryClient();
    const { data: products, isLoading, refetch } = useGetCustomerProductsPrices(cid, dateObjectToString(date), checked, true,true,sold ? dateObjectToString(sold):undefined);
    const productsRef = React.useRef(products);
    productsRef.current = products;
    React.useEffect(() => {
        if (!isLoading) {

            cache.removeQueries(CUSTOMER_PRODUCTS);

            refetch();
        }

    }, [date, checked,sold]);
    React.useEffect(() => {
       setToDate(new Date())

    }, [radio]);

    const customer = useSelector((state: RootState) => selectCustomerById(state, cid));
    React.useEffect(() => {
        if (!customer) dispatch(getSpecificCustomer(Number(cid)));
    }, [dispatch]);

    React.useEffect(() => {
        const newCols = cloneDeep(columns);
        // A menu (id <= -10) is a closed availability list. Keep זמין להזמנה on screen
        // so a product with no saved row is visibly unavailable, not an implied tick.
        if (Number(cid) <= -10) {
            const availCol = newCols.find((col) => col.id === 'available');
            if (availCol) availCol.hide = false;
        }

        // pnotes2
        const pnotes2 = JSDB().getUser().first?.getJsonDesc("pnotes2");
        let labelPnotes2 = "הערות מוצר 2";
        if (pnotes2) {
            labelPnotes2 = pnotes2.name;
        }
        newCols.push({
            id: "notes2",
            label: labelPnotes2,
            numeric: false,
            type: 'numOrStr',
            hide: true,
            align: 'center',
        });

        // pnotes3
        const pnotes3 = JSDB().getUser().first?.getJsonDesc("pnotes3");
        let labelPnotes3 = "הערות מוצר 3";
        if (pnotes3) {
            labelPnotes3 = pnotes3.name;
        }
        newCols.push({
            id: "notes3",
            label: labelPnotes3,
            numeric: false,
            type: 'numOrStr',
            hide: true,
            align: 'center',
        });

        // pnotes4
        const pnotes4 = JSDB().getUser().first?.getJsonDesc("pnotes4");
        let labelPnotes4 = "הערות מוצר 4";
        if (pnotes4) {
            labelPnotes4 = pnotes4.name;
        }
        newCols.push({
            id: "notes4",
            label: labelPnotes4,
            numeric: false,
            type: 'numOrStr',
            hide: true,
            align: 'center',
        });

        // pnotes5
        const pnotes5 = JSDB().getUser().first?.getJsonDesc("pnotes5");
        let labelPnotes5 = "הערות מוצר 5";
        if (pnotes5) {
            labelPnotes5 = pnotes5.name;
        }
        newCols.push({
            id: "notes5",
            label: labelPnotes5,
            numeric: false,
            type: 'numOrStr',
            hide: true,
            align: 'center',
        });
        const pnotes6 = JSDB().getUser().first?.getJsonDesc("pnotes6");
        let labelPnotes6 = "הערות מוצר 6";
        if (pnotes6) {
            labelPnotes6 = pnotes6.name;
        }
        newCols.push({
            id: "notes6",
            label: labelPnotes6,
            numeric: false,
            editable: true,
            type: 'numOrStr',
            hide: true,
            priority: -1,
        });

        setHead(newCols);
    }, [cid]);


    const copyFromClient = React.useCallback(
        async (from: number, d: Date) => {
            try {
                await copyClientPrice(from, Number(cid), date, d, radio != 'from' ? toDate : undefined, radio == 'unique');
                refetch();
                dispatch({
                    type: SNACKBAR_OPEN,
                    open: true,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                    },
                    message: `מחירים הועתקו!`,
                    variant: 'alert',
                    alertSeverity: 'success'
                });

            } catch (e) {
                console.log(e);
                Swal.fire({
                    title: 'שגיאה בגישה לנתונים',
                    target: '#custom-target',
                    text: '',
                    icon: 'error',
                    showCancelButton: false,
                    confirmButtonColor: '#d63030'
                });

            }
        },
        [date, radio]
    );

    const pricesImportAdapter = React.useMemo(
        () =>
            buildCustomerPricesImportAdapter({
                cid,
                dateStr: dateObjectToString(date),
                toDateStr: toDate ? dateObjectToString(toDate) : undefined,
                unique: radio === 'unique',
            }),
        [cid, date, toDate, radio]
    );

    const collectAllProductIds = React.useCallback(() => {
        let catalog: { id?: number }[] = [];
        try {
            catalog = JSDB().getAllClientProduct(false)?.first?.toArray?.() ?? [];
        } catch (e) {
            catalog = [];
        }
        return Array.from(new Set([
            ...(products ?? []).map((p) => Number(p.id)),
            ...catalog.map((p) => Number(p.id)),
        ].filter((id) => Number.isFinite(id) && id > 0)));
    }, [products]);

    const makeAllProductsAvailable = React.useCallback(async () => {
        const ids = collectAllProductIds();
        if (!ids.length) {
            await Swal.fire({
                title: 'אין מוצרים לעדכון',
                icon: 'info',
                confirmButtonText: 'אישור',
                target: '#custom-target',
            });
            return;
        }
        const { isConfirmed } = await Swal.fire({
            title: 'להפוך את כל המוצרים לזמינים?',
            text: `${ids.length} מוצרים יסומנו כזמינים להזמנה עבור לקוח זה.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'המשך',
            cancelButtonText: 'ביטול',
            target: '#custom-target',
        });
        if (!isConfirmed) return;
        try {
            setIsMutating(true);
            await updateCustomerProductAvailable([Number(cid)], ids, undefined, true);
            refetch();
            dispatch({
                type: SNACKBAR_OPEN,
                open: true,
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                },
                message: 'כל המוצרים סומנו כזמינים',
                variant: 'alert',
                alertSeverity: 'success',
            });
        } catch (e) {
            console.log(e);
            Swal.fire({
                title: 'שגיאה בגישה לנתונים',
                target: '#custom-target',
                text: '',
                icon: 'error',
                showCancelButton: false,
                confirmButtonColor: '#d63030',
            });
        } finally {
            setIsMutating(false);
        }
    }, [collectAllProductIds, cid, refetch, dispatch]);

    const applyDefaultBaseFromProducts = React.useCallback(async () => {
        if (!products?.length) return;
        const { isConfirmed } = await Swal.fire({
            title: 'להחיל מחיר בסיס מהמוצר?',
            text: 'המחיר יעודכן לפי מחיר ברירת המחדל של כל מוצר; ההנחות הקיימות לכל שורה יישמרו.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'המשך',
            cancelButtonText: 'ביטול',
            target: '#custom-target',
        });
        if (!isConfirmed) return;
        try {
            setIsMutating(true);
            const toDateTemp = radio == 'from' ? undefined : toDate;
            const payload = products.map((d) => {
                const base = roundDecimals(
                    Math.max(0, Number(d.nkObject?.product?.getDefaultPrice?.() ?? 0)),
                    4
                );
                return {
                    id: Number(cid),
                    product_id: d.id,
                    price: base,
                    discount: roundDecimals(Number(d.discount ?? 0), 4),
                    master: d.master && d.master != -1 ? d.master : -1,
                };
            });
            await updateAllPrice(
                payload,
                dateObjectToString(date),
                toDateTemp ? dateObjectToString(toDateTemp) : undefined,
                false,
                undefined,
                undefined,
                radio == 'unique'
            );
            refetch();
            dispatch({
                type: SNACKBAR_OPEN,
                open: true,
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                },
                message: 'מחירי בסיס מהמוצר הוחלו',
                variant: 'alert',
                alertSeverity: 'success',
            });
        } catch (e) {
            console.log(e);
            Swal.fire({
                title: 'שגיאה בגישה לנתונים',
                target: '#custom-target',
                text: '',
                icon: 'error',
                showCancelButton: false,
                confirmButtonColor: '#d63030',
            });
        } finally {
            setIsMutating(false);
        }
    }, [products, cid, date, toDate, radio, refetch, dispatch]);

    const [historyProduct, setHistoryProduct] = React.useState<PriceHistoryTarget | null>(null);

    const [menuItems, setMenuItems] = React.useState<{
        available: {
            label: string;
            value: boolean;
        }[]
    }>({
        available: [{
            label: "זמין",
            value: true,
        },
        {
            label: "לא זמין",
            value: false,
        },
        ],
    });

    const chipDetails = useMemo(() => {
        return [
            {
                action: () => {
                    !checked && setChecked(true);
                    checked && setChecked(false);
                },
                label: () => checked ? "הצג רק את מוצרי הלקוח" : "הצג את כל מוצרי המערכת",
                icon: <IconInfoCircle />,
                tooltip: 'הצג את כל המוצרי המערכת',
                active: () => checked,
            },

            {
                authButton: customer ? (
                    <PricesPdfExport
                        clientId={Number(customer?.nkObject?.masterBranch?.id ?? customer?.id)}
                        clientName={customer?.name ?? ''}
                        date={date}
                    />
                ) : undefined,
                action: () => {},
                label: () => "יצא לPDF",
                icon: <Avatar variant='square'
                    sx={{ cursor: 'pointer', bgcolor: 'white', width: '24px', height: '24px' }}
                    src={pdf} />,
                tooltip: 'יצא לPDF',
                active: () => false,
            },
            {
                authButton: (
                    <DialogChooseAction
                        withDate={date}
                        chooseFrom={customers.map((c) => ({ id: Number(c.id), name: c.name, externalId: c.externalId }))}
                        icon={<CopyAll />}
                        tooltip={'העתק מחירים'}
                        onClose={(v, d) => {
                            copyFromClient(v.id, d ?? date);
                        }}
                        title={'העתק מחירים'}
                        multi={false}
                        appendContent={
                            customer && customer.priceControl > -10
                                ? ({ close }) => (
                                    <Grid container sx={{ mt: 1.5 }}>
                                        <Grid item xs={12}>
                                            <Tooltip title="החל מחיר בסיס ממחיר ברירת המחדל של המוצר (בכרטיס המוצר)">
                                                <Button
                                                    fullWidth
                                                    variant="outlined"
                                                    startIcon={<PriceChangeOutlined />}
                                                    onClick={() => {
                                                        close();
                                                        void applyDefaultBaseFromProducts();
                                                    }}
                                                >
                                                    מחיר בסיס ממוצר
                                                </Button>
                                            </Tooltip>
                                        </Grid>
                                    </Grid>
                                )
                                : undefined
                        }
                    />
                ),

                action: () => {

                },
                label: () => "",
                icon: <div></div>,

                tooltip: '',
                active: () => false,
            },
            // {
            //     authButton: <AvailabilityCopyDialog icon={<CopyAll />} tooltip="העתק זמינות מוצרים ללקוחות/קבוצות/קווים" />,
            //     action: () => {

            //     },
            //     label: () => "",
            //     icon: <div></div>,
            //     tooltip: '',
            //     active: () => false,
            // },
            {
                label: () => "רק מוצרים זמינים",
                action: (params) => {
                    setOnlyAvailable(!onlytAvailable)
                    localStorage.setItem('onlyAvailable', (!onlytAvailable).toString());
                },
                icon: <CheckCircleOutlinedIcon />,
                tooltip: "הצג האם המוצר זמין להזמנה",
                active: () => onlytAvailable,
            },
            {
                label: () => "הפוך הכל לזמין",
                action: () => {
                    void makeAllProductsAvailable();
                },
                icon: <CheckCircleOutline />,
                tooltip: "סמן את כל המוצרים כזמינים להזמנה",
                active: () => false,
            },
            {
                label: () => "רק מוצרים שנמכרו",
                action: (params) => {
                    setSold(prevState =>
                     prevState ? undefined: toDate
                    )

                },
                icon: <CheckCircleOutlinedIcon />,
                tooltip: "הצג האם המוצר נמכר ללקוח",
                active: () => sold,
            },
            ...(customer && customer.priceControl > -10
                ? [
                      {
                          authButton: (
                              <ImportWizardTrigger
                                  chipLabel="מחירים מקובץ"
                                  adapter={pricesImportAdapter}
                                  currentData={productsRef.current}
                                  onFinished={() => {
                                      void refetch();
                                  }}
                              />
                          ),
                          action: () => {
                              /* chip rendered via authButton */
                          },
                          label: () => '',
                          icon: <div />,
                          tooltip: '',
                          active: () => false,
                      } as const,
                  ]
                : []),
        ]
    }, [checked, customer, date, copyFromClient, applyDefaultBaseFromProducts, makeAllProductsAvailable, onlytAvailable, sold, toDate, pricesImportAdapter, refetch]);

    const saveDataCallback = React.useCallback(async (data) => {
        try {
            const toDateTemp = radio == 'from' ? undefined : toDate
            const normAvail = (v: unknown) => (Number(v) ? 1 : 0);
            const availabilityEdited = data.some((d) => {
                const orig = products?.find((p) => p.id === d.id);
                return normAvail(orig?.available) !== normAvail(d.available);
            });
            const availables = availabilityEdited ? data.map((d) => Number(d.available)) : undefined;
            const x = await updateAllPrice(data.map(d => {
                const client = JSDB().getClient(Number(cid)).first

                return ({
                    id:  Number(cid),
                    product_id: d.id,
                    price: d.totalPrice,
                    discount: d.discount,
                    master:d.master && d.master!=-1 ? d.master : -1

                })
            }), dateObjectToString(date), toDateTemp ? dateObjectToString(toDateTemp) : undefined, false,undefined,availables,radio=="unique");
            refetch()
            return true
        } catch (e) {
            return false
        }

    }, [date, toDate, cid, radio, products]);
    const selectionControls = useCallback((selected) => {
        return (
            customer && customer.priceControl <= -10 ? <MultiProductsActions selected={selected?.map((p) => p?.id)}
                                                                             customer={customer}
                                                                             ids={[Number(cid)]} date={date} toDate={toDate}
                                                                             radio={radio}
                                                                             noPrice={true}
                                                                             extractor={(x) => x?.id} /> : <div style={{ display: "flex", position: "absolute", left: 0, marginTop: -35, top: 0 }}>
                <MultiProductsActions selected={selected?.map((p) => p?.id)}
                    customer={customer}
                    ids={[Number(cid)]} date={date} toDate={toDate}
                    radio={radio}
                    extractor={(x) => x?.id} />
            </div>

        )
    }, [customer, date, toDate, cid, radio]);

    const tableControls: ITableControls = {
        tableName: "costumers/costumers-list/costumers-prices",
        sidebar: true,
        allowEditSameValue: true,
        groupBy: {
            enable: true,
            rowGroup: true,
        },
        chipDisplayAmount: 8,
        disableEdit: customer && customer.priceControl <= -10,
        selection: {
            enable: true,
            mode: "multiple",
        },
        cellRenderer: (params: any, column: any, i: number) => {
            if (params?.node?.rowPinned) return params.value || "";
            if (column.id === 'name' && !params?.node?.group) {
                const name = params?.value ?? params?.data?.name ?? '';
                return (
                    <Box
                        sx={{
                            display: 'inline-flex',
                            flexDirection: 'row',
                            direction: 'ltr',
                            alignItems: 'center',
                            gap: 0.25,
                            alignSelf: 'flex-start',
                            maxWidth: '100%'
                        }}
                    >
                        <span>{name}</span>
                        <Tooltip title="היסטוריית מחיר">
                            <IconButton
                                size="small"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    if (!params?.data?.id) return;
                                    setHistoryProduct({
                                        productId: Number(params.data.id),
                                        productName: String(params.data.name ?? name),
                                        master: params.data.master
                                    });
                                }}
                            >
                                <HistoryOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            }
            if (column.cellRenderer) {
                return column.cellRenderer({
                    params,
                });
            }
            if (typeof params?.value === "object") {
                const value = params?.value?.value || "";
                return value;
            }
            return params.value || "";
        },
    };
    return (
        <>
            {(isLoading || isMutating) && <Loader />}
            <Box sx={{ pb: 3 }}>
                {/*<ReactVirtualizedTable/>*/}
                <PricesFilterBar
                    // checked={checked} handleChecked={handleChecked}
                    radio_value={radio}
                    handleRadioChange={setRadio} date={date} setDate={setDate} toDate={toDate}
                    setToDate={setToDate} data={products} titles={columns} customer={customer} />
            </Box>
            <AgTable
                data={(onlytAvailable ? products?.filter((p)=>p.available) : products) ?? []}
                columns={heads}
                tableControls={tableControls}
                chipDetails={chipDetails}
                saveDataCallback={saveDataCallback}
                selectionControls={selectionControls}
                pinnedControl={basicPinnedControl()}
                menuiItems={menuItems}
            />
            <PriceHistoryDialog
                open={Boolean(historyProduct)}
                onClose={() => setHistoryProduct(null)}
                clientId={Number(cid)}
                product={historyProduct}
                canEdit={!(customer && customer.priceControl <= -10)}
                onSaved={() => { void refetch(); }}
            />
        </>
    );
};

export default memo(PricesList);
